import { AfterViewInit, Component, OnDestroy } from '@angular/core';
import { NotificationService } from './shared/modules/notification/notification.service';
import { AuthService, ValidationStatus } from './shared/services/AuthService';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { EventService } from './shared/services/event.service';
import { lastValueFrom, Subscription } from 'rxjs';
import { RestApiService } from './shared/services/RestApiServices.service';
import { EventWorkerService } from './shared/services/worker/event-worker/event-worker.service';
import { TranslateService } from '@ngx-translate/core';
import { MiniLoaderService } from './shared/modules/mini-loader/mini-loader.service';
import { CompanyType } from './shared/models/Enums';
import { ToastService } from './shared/modules/toast/toast.service';
import { RealtimeService } from './shared/services/realtime.service';
import { EventCommonService } from './pages/content/event/event-common.service';
import { UserResponse } from './shared/models/users.model';
import { LauncherUpdateGuardService } from './shared/services/launcher-update-guard.service';
import { AppUpdateCheckService } from './shared/services/app-update-check.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements AfterViewInit, OnDestroy {
  // call this event handler before browser refresh
  // @HostListener('window:beforeunload', ['$event'])
  // unloadHandler(event: Event) {
  //   this.processData();
  // }
  readonly ValidationStatus = ValidationStatus;

  showStartPage: boolean;
  user: UserResponse;

  validationSub$?: Subscription;
  private connectionStateSub?: Subscription;
  private streamDropped = false;
  private eventChangedSub?: Subscription;
  private syncHeartbeat?: ReturnType<typeof setInterval>;
  private syncEventCount = 0;
  resetPasswordMode: boolean = false;

  code: string = '';
  email: string = '';

  constructor(
    public authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
    private route: ActivatedRoute,
    private eventService: EventService,
    private restApiService: RestApiService,
    private eventWorkerService: EventWorkerService,
    private translate: TranslateService,
    public loaderService: MiniLoaderService,
    private toastService: ToastService,
    private realtimeService: RealtimeService,
    private eventCommonService: EventCommonService,
    public launcherUpdateGuard: LauncherUpdateGuardService,
    private appUpdateCheck: AppUpdateCheckService,
  ) {
    console.log(`Version: ${environment.appVersion}`);
    this.appUpdateCheck.start();

    if (!sessionStorage.getItem('BayManager-TabId')) {
      var tabId = 'TabId:' + Math.floor(1000 + Math.random() * 9000);
      sessionStorage.setItem('BayManager-TabId', tabId);
    }

    this.notificationService.extendMinutes$.subscribe((extended) => {
      if (extended && extended.approved && extended.simId) {
        this.eventService.extendMinuteFromUpcoming(extended);
        this.eventWorkerService.resetWorker();
      }
    });

    this.eventChangedSub = this.realtimeService.eventChanged$.subscribe(() => {
      this.syncEventCount++;
    });
    this.syncHeartbeat = setInterval(() => {
      const n = this.syncEventCount;
      this.syncEventCount = 0;
      console.log(
        n > 0
          ? `[Sync] Calendar synced with uSchedule - ${n} event(s) added/updated in the last 5 min`
          : '[Sync] Calendar synced with uSchedule - no changes in the last 5 min',
      );
    }, 5 * 60 * 1000);

    this.addListeners();
    this.getQueryParams();
  }

  ngAfterViewInit(): void {
    setTimeout(async () => {
      try {
        if (this.resetPasswordMode) {
          this.loaderService.loader = false;
          await this.authService.unautorized();
          return;
        }

        this.showStartPage = true;
        this.loaderService.loader = true;
        this.authService.validationResult = ValidationStatus.Invalid;

        const tokenValidationResult = await this.authService.isTokenValid();

        if (!tokenValidationResult) {
          await this.unautorized();
          return;
        }

        const isAuth = await this.authService.isAuthenticatedCognitoGroups();

        if (!isAuth) {
          this.toastService.error(this.translate.instant('userNotBMAdmin'));
          await this.unautorized();
          return;
        }

        const userId = await this.authService.getUserId();

        if (!userId?.length) {
          await this.unautorized();
          return;
        }

        this.user = await lastValueFrom(
          this.restApiService.getUserByUserId(userId),
        );

        if (!this.user?.id || !this.user?.cognitoId?.length) {
          await this.unautorized();
          return;
        }

        if (this.user?.company?.companyType !== CompanyType.Commercial) {
          this.toastService.warning(
            this.translate.instant('userNotCommercial'),
          );
          await this.unautorized();
          return;
        }

        if (
          !this.user ||
          !this.user.company?.isBayManager ||
          !this.user.location?.isBayManager ||
          !this.user.baymanagerAdmin
        ) {
          if (!this.user) {
            this.toastService.error(this.translate.instant('loginError'));
          } else if (!this.user.company?.isBayManager) {
            this.toastService.warning(this.translate.instant('companyNotBM'));
          } else if (!this.user.location?.isBayManager) {
            this.toastService.warning(this.translate.instant('usersLocNotBM'));
          } else if (!this.user.baymanagerAdmin) {
            this.toastService.warning(this.translate.instant('userNotBM'));
          }

          await this.unautorized();
          return;
        }

        this.restApiService.user = this.user;

        this.restApiService.initialDBObjects();
        this.notificationService.requestAndShowPermission();
        this.launcherUpdateGuard.beginCheck(this.user.company.id);
        await this.initialEvents(this.user.company.id);
        this.realtimeService.connect(this.user.company.id);
        void this.launcherUpdateGuard.start(this.user.company.id);

        this.connectionStateSub = this.realtimeService.connectionState$.subscribe(
          (state) => {
            if (state === 'reconnecting' || state === 'closed') {
              this.streamDropped = true;
            } else if (state === 'open' && this.streamDropped) {
              this.streamDropped = false;
              this.resyncAfterReconnect();
            }
          },
        );

        this.authService.validationResult = ValidationStatus.Valid;
        this.initialValidationSub();

        this.showStartPage = false;

        const urlLength = window.location.pathname.length;
        if (urlLength < 2) {
          this.router.navigate([environment.defaultPage]);
        }
      } catch (error) {
        await this.unautorized();
        console.error(error);
      } finally {
        this.loaderService.loader = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.validationSub$?.unsubscribe();
    this.connectionStateSub?.unsubscribe();
    this.eventChangedSub?.unsubscribe();
    if (this.syncHeartbeat) {
      clearInterval(this.syncHeartbeat);
    }
    this.realtimeService.disconnect();
    this.launcherUpdateGuard.stop();
  }

  async unautorized(): Promise<void> {
    if (this.resetPasswordMode) {
      this.showStartPage = false;
      this.router
        .navigate(['login'], {
          queryParams: {
            code: this.code,
            email: this.email,
          },
        })
        .then(async (_) => {
          this.loaderService.loader = false;
          await this.authService.unautorized();
        });
    } else {
      this.showStartPage = true;
      this.loaderService.loader = false;
      await this.authService.unautorized();
    }
  }

  addListeners(): void {
    //let self: this;

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        console.log('tab status : ' + document.visibilityState);
      } else {
        console.log('tab status : ' + document.visibilityState);
      }
    });

    window.addEventListener('pageshow', function (event) {
      console.log('pageshow ', event);
      if (event.persisted) {
        // comes from cache
      }
    });
  }

  getQueryParams(): void {
    this.route.queryParams.subscribe((params) => {
      this.email = params['email'] ?? '';
      this.code = params['code'] ?? '';

      if (this.email.indexOf(' ') > -1) {
        this.email = this.email.replace(/ /g, '+');
      }

      if (this.email?.length && this.code?.length) {
        this.resetPasswordMode = true;
      }
    });
  }

  checkAuthentication(): void {
    this.showStartPage = false;
    this.router.navigate(['login']);
  }

  processData(): void {
    // store data into local storage before browser refresh
    window.localStorage.removeItem(environment.bayManagerUser);
  }

  initialValidationSub(): void {
    this.validationSub$ = this.authService.validationSub$.subscribe((value) => {
      if (value === ValidationStatus.Invalid) {
        this.realtimeService.disconnect();
        this.launcherUpdateGuard.stop();
      }
    });
  }

  /**
   * Initial upcoming events and worker service
   *
   */
  async initialEvents(companyId: number): Promise<void> {
    try {
      await this.eventService.loadAllUpcomingEvents(companyId);
      this.eventWorkerService.resetWorker();
    } catch (error) {
      console.error(error);
    }
  }

  private async resyncAfterReconnect(): Promise<void> {
    try {
      await this.initialEvents(this.user.company.id);
      await this.realtimeService.refreshOnlineSimulators();
    } catch (error) {
      console.error(error);
    }
  }
}
